import React, { useEffect, useMemo, useState } from "react";
import classNames from 'classnames';
import { IconButton, Paper, Popper, Box, Typography, Button } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import CodeIcon from "@material-ui/icons/Code";
import FilterListIcon from "@material-ui/icons/FilterList";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import { DateRangePicker, FilterMultiSelect, RangeSlider } from 'dsilo-ui-components';

// Helper function to check if value is an object
const isObject = (field) => {
    return field && typeof field === 'object' && !Array.isArray(field) && field !== null
}

const useStyles = makeStyles((theme) => {
    const isNewV1Theme = theme?.isNewV1Theme || true;

    return {
        chipSelect: {
            minWidth: 250,
            marginTop: 10,
        },
        popper: {
            padding: isNewV1Theme ? '16px 17px' : "16px"
        },
        planFilter: {
            border: '1px solid #ddd',
            borderRadius: 8,
            paddingLeft: 10,
            minWidth: 100,
            fontWeight: 'bold',
            textAlign: 'center',
            whiteSpace: 'nowrap',
            color: isNewV1Theme ? '#5F6071' : "",
        },
        active: {
            border: '1px solid tomato'
        },
        clearAll: {
            fontWeight: 500,
            alignSelf: 'center',
            marginTop: 16,
            cursor: "pointer",
            minWidth: 70
        },
        filterTitleSize: {
            ...(isNewV1Theme ? {
                fontSize: '12px',
                color: '#5F6071',
                fontWeight: '400 !important',
            } : { fontSize: '13px', whiteSpace: 'nowrap' }),
        },
        filterGrid: {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: isNewV1Theme ? '10px' : '24px 32px',
            alignItems: 'start',
        },
        popoverContent: {
            padding: isNewV1Theme ? '16px 17px' : '16px',
        },
        footer: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingTop: '16px',
            borderTop: '1px solid #e0e0e0',
            marginTop: '16px'
        },
        save: {
            backgroundColor: '#1976d2',
            color: 'white',
            textTransform: 'capitalize',
            height: 32,
            boxSizing: 'border-box',
            '&:disabled': {
                backgroundColor: 'rgba(0, 0, 0, 0.12)',
            }
        }
    }
});

const ExternalFilter = props => {
    const classes = useStyles();

    const getInitialVal = () => {
        let obj = {};
        props.externalFilters?.forEach(ii => {
            obj[ii.value] = null
        })
        return obj
    }

    const [anchorEl, setAnchorEl] = useState(null)
    const [anchorRef, setAnchorRef] = useState(null)
    const [filterValues, setFilterValues] = useState(getInitialVal())
    const [localExternalFilterData, setLocalExternalFilterData] = useState(null)

    const open = Boolean(anchorRef);
    let _conditions = props?.conditions || [];

    useEffect(() => {
        generatelocalExternalFilterData()
    }, [props.externalFilterData, props?.formData?.externalFilterData])

    useEffect(() => {
        if (props.externalFilterValues) {
            setFilterValues({ ...filterValues, ...props.externalFilterValues })
        }
    }, [props.externalFilterValues])

    const generatelocalExternalFilterData = () => {
        let refData = localExternalFilterData
        if (props.externalFilters && props?.externalFilterData?.length > 0) {
            if (!refData) {
                refData = {}
            }
            props.externalFilters.map((fil, index) => {
                if (props?.externalFilterData && props?.externalFilterData[index]?.length > 0) {
                    refData[fil.value] = [...props.externalFilterData[index]];
                } else {
                    refData[fil.value] = null
                }
            })
        }
        setLocalExternalFilterData({ ...refData })
    }

    const onClose = (isSave, plans, e) => {
        if (anchorEl?.current && anchorEl.current.contains(e?.target)) {
            return;
        }
        setAnchorEl(null)
    }

    const onFilterChange = (name, value) => {
        filterValues[name] = value
        setFilterValues({ ...filterValues })
        if (props?.setLocalSelectedFilterData) props?.setLocalSelectedFilterData(filterValues)

        // Only update externalFilterValues immediately if not in vertical mode
        // In vertical mode, filters should only be applied when Save is clicked
        const isVerticalAlignment = props?.align === 'vertical';
        if (!isVerticalAlignment && props?.setExternalFilterValues) {
            props?.setExternalFilterValues(filterValues)
        }
        if (!isVerticalAlignment && props.onExternalFilterChange) {
            props.onExternalFilterChange(filterValues)
        }
    }

    const handleMultiFilterClick = (event, index) => {
        if (anchorEl && anchorEl[index]) {
            setAnchorEl(null);
        } else {
            let externalFilters = props.externalFilters
            let field = externalFilters && externalFilters[index] && externalFilters[index].value;
            if (field && (!localExternalFilterData || !localExternalFilterData[field])) {
                fetchFilterData(externalFilters[index])
            }
            setAnchorEl({ [index]: event.currentTarget });
        }
    }

    const fetchFilterData = (externalFilter) => {
        if (externalFilter && props.dataModelId) {
            let payloadForDropDown = {
                appId: props.appId,
                id: props.dataModelId,
                rowsPerPage: "all",
                conditions: _conditions
            };

            payloadForDropDown.field = externalFilter.value
            payloadForDropDown['dataUniqueId'] = props.dataUniqueId + '_externalFilter' + '_' + externalFilter.value;

            if (props.getModelDataDropDown) {
                return props.getModelDataDropDown({
                    ...payloadForDropDown,
                    dataUniqueId: payloadForDropDown['dataUniqueId']
                })
            }
        }
    }

    const isLoading = (externalFilter) => {
        return !!props.formData?.[props.dataUniqueId + '_externalFilter' + '_' + externalFilter.value + "_loading"]
    }

    const getFilterValue = (name, type) => {
        let filterFound = filterValues[name];
        if (type === 'date' && filterFound) {
            if (filterFound.value) {
                return { startDate: filterFound.value?.split(',')[0], endDate: filterFound.value?.split(',')[1] }
            } else {
                return { startDate: filterFound.startDate, endDate: filterFound.endDate }
            }
        }
        if (type === 'number' && filterFound) {
            return filterFound.value
        }
        if (type === 'namepicker' && filterFound) {
            return filterFound.value
        }
        if (filterFound) {
            return filterFound.value
        }
        return null;
    }

    const getNumberFilterValues = (value) => {
        let _data = localExternalFilterData && localExternalFilterData[value]
        if (_data) {
            let values = []
            _data.forEach(ii => {
                if (ii.value && ii.value?.replace) {
                    let val = ii.value?.replace(',', '')?.replace('$', '')
                    values.push(parseFloat(val))
                } else if (ii.value) {
                    values.push(parseFloat(ii.value))
                }
            })
            return { min: Math.min.apply(null, values), max: Math.max.apply(null, values) || 0 }
        }
        return { min: 0, max: 0 }
    }

    const isVerticalAlignment = useMemo(() => {
        return props?.align === 'vertical'
    }, [props?.align])

    const hasLocalFilterValues = () => {
        return Object.values(filterValues).some(filter =>
            filter && filter.value && filter.value.length > 0
        );
    }

    return (
        <div style={
            props?.disableOuterChrome
                ? {}
                : (isVerticalAlignment
                    ? { padding: props?.isNewV1Theme ? '16px 17px' : '16px', borderTop: props?.isNewV1Theme ? '1px solid #E8E8E8' : '' }
                    : { display: 'flex' })
        }>
            <div
                style={
                    isVerticalAlignment
                        ? {
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: props?.isNewV1Theme ? '10px' : '24px 32px',
                            alignItems: 'start',
                        }
                        : {
                            display: 'flex',
                            overflowX: 'auto',
                            flexWrap: 'nowrap',
                            overflowY: 'hidden'
                        }
                }
            >
                {
                    Boolean(props.externalFilters?.length) &&
                    props.externalFilters.map((filter, index) => {
                        const wrapperStyle = isVerticalAlignment
                            ? { display: 'flex', flexDirection: 'column' }
                            : { marginRight: 10, alignSelf: 'center' };

                        const label = (
                            <span
                                className={classes.filterTitleSize}
                                style={isVerticalAlignment ? props?.isNewV1Theme ? { marginBottom: 6, fontWeight: 400, color: '#78798E ', } : { marginBottom: 8, fontWeight: 500 } : {}}
                            >
                                {filter.label}
                            </span>
                        );

                        if (filter.type === 'string' || filter.type === 'namepicker' || filter.type === "arrayOfStrings" || filter.type === 'select') {
                            let filterData = [...(localExternalFilterData?.[filter.value] || [])] || [];
                            const valueObj = filterValues[filter.value]?.length;
                            const selectedValues = filterValues[filter.value] || [];

                            if (filter?.type !== "arrayOfStrings") {
                                filterData = filterData?.map(i => {
                                    // if (i.value && !isObject(i.value)) i.value = i.value.toString();
                                    if (i.value != null && !isObject(i.value) && typeof i.value !== 'number') {
                                        i.value = String(i.value);
                                    }
                                    return i;
                                });
                            }

                            const truncateText = (text, maxLength = 25) => {
                                if (!text || text.length <= maxLength) return text;
                                return `${text}`.substring(0, maxLength) + '...';
                            };

                            const getDisplayText = () => {
                                if (!selectedValues || selectedValues?.length === 0) {
                                    return `--All ${filter.label}--`;
                                }

                                if (selectedValues.length === 1) {
                                    const firstSelected = selectedValues[0];
                                    if (firstSelected?.label) {
                                        return truncateText(firstSelected.label);
                                    }

                                    const selectedItem = filterData.find(item => {
                                        if (item.value?.id) {
                                            return selectedValues.some(sv => sv.value?.id === item.value?.id);
                                        } else {
                                            return selectedValues.some(sv => sv.value === item.value);
                                        }
                                    });
                                    return selectedItem ? truncateText(selectedItem.label) : `--All ${filter.label}--`;
                                }

                                let firstSelectedItem = null;
                                if (filterData && filterData.length > 0) {
                                    if (props?.isNewV1Theme) {
                                        firstSelectedItem = filterData.find(item => {
                                            if (item.value?.id) {
                                                return selectedValues.some(sv => sv.value?.id === item.value?.id);
                                            } else {
                                                return selectedValues.some(sv => sv.value === item.value);
                                            }
                                        });
                                    } else {
                                        firstSelectedItem = filterData.find(item => {
                                            if (item.value?.id) {
                                                return selectedValues.some(sv => sv.value?.id === item.value?.id && sv.label);
                                            } else {
                                                return selectedValues.some(sv => sv.value === item.value && sv.label);
                                            }
                                        });
                                    }
                                } else {
                                    if (props?.isNewV1Theme) {
                                        firstSelectedItem = selectedValues[0];
                                    } else {
                                        firstSelectedItem = selectedValues.find(sv => sv.label);
                                    }
                                }

                                const truncatedLabel = firstSelectedItem
                                    ? truncateText(firstSelectedItem.label, 20)
                                    : `${selectedValues.length} ${filter.label}`;
                                return `${truncatedLabel} +${selectedValues.length - 1} more`;
                            };

                            return (
                                <div key={index} style={wrapperStyle}>
                                    {label}
                                    <div
                                        onClick={(e) => handleMultiFilterClick(e, index)}
                                        className={classNames(classes.planFilter, !!valueObj ? classes.active : '')}
                                        style={
                                            isVerticalAlignment
                                                ? {
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    border: props?.isNewV1Theme
                                                        ? (!!valueObj ? '1px solid #1976d2' : '1px solid #D5D6DD')
                                                        : (!!valueObj ? '1px solid #1976d2' : '1px solid #ccc'),
                                                    borderRadius: 4,
                                                    padding: props?.isNewV1Theme ? '5px 8px 6px 8px' : '8px 12px',
                                                    cursor: 'pointer',
                                                    color: props?.isNewV1Theme ? '#5F6071 !important' : '',
                                                    backgroundColor: !!valueObj ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
                                                }
                                                : {}
                                        }
                                    >
                                        <span className={classes.filterTitleSize}>{getDisplayText()}</span>
                                        <IconButton style={{ padding: isVerticalAlignment ? 4 : 10 }}>
                                            {props?.isNewV1Theme ? (
                                                <ArrowDropDownIcon style={{ fontSize: isVerticalAlignment ? 16 : 14 }} />
                                            ) : (
                                                <FilterListIcon style={{ fontSize: isVerticalAlignment ? 16 : 14 }} />
                                            )}
                                        </IconButton>
                                    </div>

                                    {anchorEl?.[index] && (
                                        <FilterMultiSelect
                                            allValues={filterData}
                                            anchorEl={anchorEl[index]}
                                            id="transitions-popper"
                                            selectedValues={filterValues[filter.value] || []}
                                            onClose={onClose}
                                            isOpen={anchorEl[index]}
                                            onSave={(v, clearAllFlag) => {
                                                clearAllFlag ? null : setAnchorEl(null);
                                                onFilterChange(filter.value, v);
                                            }}
                                            onSelectAllChange={(isSelectAll) => {
                                                if (props.onSelectAllChange) {
                                                    props.onSelectAllChange(filter.value, isSelectAll);
                                                }
                                            }}
                                            showLoader={isLoading(props.externalFilters[index])}
                                            filterData={filterData}
                                            loader={props.loader}
                                            isNewV1Theme={props?.isNewV1Theme}
                                        />
                                    )}
                                </div>
                            );
                        }

                        if (filter.type === 'date') {
                            const dateValue = getFilterValue(filter.value, 'date');
                            const hasDateFilter = dateValue && (dateValue.startDate || dateValue.endDate);
                            return (
                                <div key={index}
                                    style={{
                                        ...wrapperStyle, ...(props?.isNewV1Theme
                                            ? {
                                                border: hasDateFilter ? "1px solid #1976d2" : "1px solid #E9EBF5",
                                                borderRadius: "4px",
                                                padding: "12px",
                                            }
                                            : {}),
                                    }}>
                                    {label}
                                    <DateRangePicker
                                        labelFontSize={classes.filterTitleSize}
                                        label={''}
                                        dateRange={getFilterValue(filter.value, 'date')}
                                        onDateRangeChange={(val) => onFilterChange(filter.value, val)}
                                        isActive={hasDateFilter}
                                        isNewV1Theme={props?.isNewV1Theme}
                                    />
                                </div>
                            );
                        }

                        if (filter.type === 'number') {
                            const numberValue = getFilterValue(filter.value, 'number');
                            const hasNumberFilter = numberValue && Array.isArray(numberValue) && numberValue.length === 2;
                            return (
                                <div key={index} style={wrapperStyle}>
                                    {label}
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: hasNumberFilter ? '8px' : '0px',
                                        padding: hasNumberFilter ? '4px 8px' : '4px',
                                        border: hasNumberFilter ? '1px solid #1976d2' : '1px solid transparent',
                                        borderRadius: '4px',
                                        backgroundColor: hasNumberFilter ? 'rgba(25, 118, 210, 0.04)' : 'transparent'
                                    }}>
                                        {hasNumberFilter && (
                                            <span style={{
                                                fontSize: '12px',
                                                color: '#5F6071',
                                                fontStyle: 'italic',
                                                whiteSpace: 'nowrap',
                                            }}>
                                                {numberValue[0]} - {numberValue[1]}
                                            </span>
                                        )}
                                        <IconButton
                                            style={isVerticalAlignment ? { padding: 0 } : { padding: 0 }}
                                            title="Open Slider"
                                            onClick={(e) => {
                                                handleMultiFilterClick(e, index);
                                                setAnchorRef(anchorRef ? null : e.currentTarget);
                                            }}
                                        >
                                            <CodeIcon />
                                        </IconButton>
                                    </div>
                                    {open && (
                                        <Popper open={true} anchorEl={anchorRef} style={{ zIndex: 5 }}>
                                            <Paper className={classes.popper}>
                                                <RangeSlider
                                                    onRangeSelect={(val) => {
                                                        setAnchorRef(null);
                                                        onFilterChange(filter.value, val);
                                                    }}
                                                    isLoading={isLoading(props.externalFilters[index])}
                                                    range={getNumberFilterValues(filter.value)}
                                                    value={getFilterValue(filter.value, 'number')}
                                                    label={filter.label}
                                                />
                                            </Paper>
                                        </Popper>
                                    )}
                                </div>
                            );
                        }

                        return null;
                    })
                }
            </div>
        </div>
    );
};

export default ExternalFilter;